<template>
	<div class="group-header">
		<!-- Dialog de Edição -->
		<v-dialog width="500px" v-model="showEditGroup">
			<v-card>
				<v-card-title>
					<v-icon>mdi-pencil</v-icon> Editar Grupo
				</v-card-title>
				<v-card-text>
					<v-img 
						v-if="grupoComputed.imagem_grupo" 
						width="100px" 
						contain 
						height="auto" 
						class="mx-auto rounded-sm mb-4"
						:src="`https://api.mylegis.com.br/assets/${grupoComputed.imagem_grupo}`" 
					/>
					<h3><v-icon small>mdi-pencil</v-icon> Nome do Grupo</h3>
					<v-text-field 
						:color="dynamicColor" 
						v-model="edit.nome_grupo"
					></v-text-field>

					<h3><v-icon small>mdi-image</v-icon> Imagem</h3>
					<v-file-input 
						:color="dynamicColor" 
						v-model="edit.imagem_grupo" 
						label="Alterar Imagem"
					></v-file-input>

					<h3 v-if="user.plano === `plano-premium`">
						<v-icon small>mdi-lock</v-icon> Restrito
					</h3>
					<v-checkbox 
						v-if="user.plano === `plano-premium`" 
						:color="dynamicColor" 
						v-model="edit.restrito"
						label="Grupo Restrito (participantes não podem exportar conteúdos do grupo)" 
					/>
				</v-card-text>
				<v-card-actions class="d-flex justify-end">
					<v-btn text @click="showEditGroup = false">
						Cancelar
					</v-btn>
					<v-btn 
						:loading="loadingEdit" 
						:color="dynamicColor" 
						dark 
						@click="updateGroup"
					>
						<v-icon>mdi-check</v-icon>
						Salvar Grupo
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog de Detalhes -->
		<v-dialog width="500px" v-model="showGroupDetails">
			<v-card>
				<v-card-title class="d-flex align-center">
					<v-icon class="mr-2">mdi-information</v-icon>
					Detalhes do Grupo
					<v-spacer></v-spacer>
					<v-btn @click="showGroupDetails = false" text>
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<v-row>
						<v-col cols="12" class="text-center">
							<v-img 
								v-if="grupoComputed.imagem_grupo" 
								width="150px" 
								contain 
								height="auto" 
								class="mx-auto rounded-sm mb-4"
								:src="`https://api.mylegis.com.br/assets/${grupoComputed.imagem_grupo}`" 
							/>
						</v-col>
						<v-col cols="12">
							<h3 class="mb-2">Nome do Grupo</h3>
							<p class="text-h6">{{ grupoComputed.nome_grupo }}</p>
						</v-col>
						<v-col cols="12">
							<h3 class="mb-2">Criado por</h3>
							<p>{{ grupoComputed.user_created }}</p>
						</v-col>
						<v-col cols="12">
							<h3 class="mb-2">Total de Integrantes</h3>
							<p>{{ grupoComputed.integrantes ? grupoComputed.integrantes.length : 0 }}</p>
						</v-col>
						<v-col cols="12">
							<h3 class="mb-2">Administradores</h3>
							<p>{{ grupoComputed.administradores ? grupoComputed.administradores.length : 0 }}</p>
						</v-col>
						<v-col cols="12" v-if="grupoComputed.restrito">
							<v-alert type="info" dense>
								Este é um grupo restrito. Os participantes não podem exportar conteúdos.
							</v-alert>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="justify-end">
					<v-btn text @click="showGroupDetails = false">Fechar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog Vademecuns dos Membros -->
		<v-dialog v-model="showVademecunsDialog" max-width="1200px" scrollable>
			<v-card>
				<v-card-title class="d-flex align-center" :style="{ color: dynamicColor }">
					<v-icon :color="dynamicColor" class="mr-2">mdi-account-multiple</v-icon>
					Vademecuns Pessoais dos Membros
					<v-spacer></v-spacer>
					<v-btn @click="showVademecunsDialog = false" icon>
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text style="max-height: 600px;" class="pa-4">
					<div v-if="loadingVademecuns" class="text-center py-8">
						<v-progress-circular :color="dynamicColor" indeterminate size="64"></v-progress-circular>
						<p class="mt-4 grey--text">Carregando vademecuns...</p>
					</div>

					<div v-else-if="vademecuns.length === 0" class="text-center py-8">
						<v-icon size="80" color="grey lighten-2">mdi-book-open-variant</v-icon>
						<h3 class="mt-4 mb-2 grey--text">Nenhum vademecum pessoal compartilhado</h3>
						<p class="grey--text">Os membros do grupo ainda não compartilharam vademecuns pessoais públicos.</p>
					</div>

					<v-row v-else>
						<v-col v-for="(v, index) in vademecuns" :key="v.id" cols="12" md="6">
							<v-card outlined hover @click="verVademecum(v)" style="cursor: pointer;">
								<v-card-title class="d-flex justify-space-between align-center pa-3">
									<span class="text-subtitle-1 font-weight-bold" style="word-break:break-word;">
										{{ v.titulo || `Vade-mécum ${index + 1}` }}
										<v-icon :color="v.cor || dynamicColor" size="16">mdi-circle</v-icon>
									</span>
								</v-card-title>
								<v-card-text class="pa-3">
									<div class="mb-2">
										<v-chip 
											v-if="v.leis && v.leis.length > 0" 
											x-small 
											:color="dynamicColor" 
											text-color="white" 
											class="mr-1"
										>
											{{ v.leis.length }} {{ v.leis.length === 1 ? 'Lei' : 'Leis' }}
										</v-chip>
										<v-chip 
											v-if="v.projetos_leis && v.projetos_leis.length > 0" 
											x-small 
											outlined 
											:color="dynamicColor" 
											class="mr-1"
										>
											{{ v.projetos_leis.length }} Projetos
										</v-chip>
									</div>
									<div class="d-flex align-center mt-2">
										<v-avatar size="24" class="mr-2">
											<v-img 
												v-if="v.user_created && v.user_created.avatar" 
												:src="`https://api.mylegis.com.br/assets/${v.user_created.avatar}`" 
											/>
											<v-icon v-else small>mdi-account-circle</v-icon>
										</v-avatar>
										<span class="text-caption grey--text">
											{{ v.user_created ? `${v.user_created.first_name} ${v.user_created.last_name || ''}` : 'Usuário' }}
										</span>
									</div>
								</v-card-text>
								<v-card-actions class="pa-2 d-flex justify-end">
									<v-btn x-small text :color="dynamicColor" @click.stop="verVademecum(v)">
										<v-icon small>mdi-eye</v-icon> Ver
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- Dialog Ver Vademecum Detalhado -->
		<v-dialog v-model="showVademecumDetalhes" fullscreen hide-overlay transition="dialog-bottom-transition">
			<v-card v-if="vademecumSelecionado">
				<v-toolbar dark color="grey darken-3" class="elevation-0">
					<v-toolbar-title class="text-h6 font-weight-bold">
						<v-icon :color="vademecumSelecionado.cor || dynamicColor" size="20">mdi-circle</v-icon>
						{{ vademecumSelecionado.titulo || 'Vade-mécum' }}
					</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-chip class="mr-4" small>
						<v-icon small class="mr-1">mdi-account</v-icon>
						{{ vademecumSelecionado.user_created ? `${vademecumSelecionado.user_created.first_name}` : 'Usuário' }}
					</v-chip>
					<v-btn icon @click="showVademecumDetalhes = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-toolbar>
				<v-card-text class="pa-6" style="background-color: white;">
					<div class="mb-6">
						<h3 class="text-h5 font-weight-bold mb-4" style="color: #424242;">
							Leis ({{ vademecumSelecionado.leis ? vademecumSelecionado.leis.length : 0 }})
						</h3>
						<div v-if="!vademecumSelecionado.leis || vademecumSelecionado.leis.length === 0" 
							class="text-center pa-4" 
							style="border: 2px dashed #e0e0e0; border-radius: 8px;"
						>
							<p class="mb-0 grey--text">Este vade-mécum ainda não possui legislações.</p>
						</div>
						<v-card 
							v-else 
							v-for="lei in vademecumSelecionado.leis" 
							:key="lei.id" 
							outlined 
							class="mb-3" 
							style="border-radius: 8px;"
						>
							<v-card-text class="pa-4">
								<div class="d-flex justify-space-between align-start">
									<div class="flex-grow-1">
										<h4 class="text-h6 font-weight-bold mb-2" style="color: #424242;">
											{{ lei.leis_id_lei.numero_lei }}
										</h4>
										<p class="text-body-1 mb-3" style="color: #666;">
											{{ lei.leis_id_lei.nome_lei }}
										</p>
									</div>
									<div>
										<v-chip small :color="dynamicColor" outlined>
											{{ lei.leis_id_lei.esfera_lei }}
										</v-chip>
									</div>
								</div>
							</v-card-text>
							<v-card-actions class="d-flex justify-end p-2">
								<v-btn 
									small 
									text 
									:color="dynamicColor" 
									:to="`/painel/lei/${lei.leis_id_lei.id_lei}/${sanitizeTitle(lei.leis_id_lei.numero_lei)}`"
								>
									<v-icon small>mdi-file-document</v-icon> Ver Lei
								</v-btn>
							</v-card-actions>
						</v-card>
					</div>
					<div 
						v-if="vademecumSelecionado.projetos_leis && vademecumSelecionado.projetos_leis.length > 0" 
						class="mb-6"
					>
						<h3 class="text-h5 font-weight-bold mb-4" style="color: #424242;">
							Projetos de Lei ({{ vademecumSelecionado.projetos_leis.length }})
						</h3>
						<v-card 
							v-for="projeto in vademecumSelecionado.projetos_leis" 
							:key="projeto.id" 
							outlined 
							class="mb-3" 
							style="border-radius: 8px;"
						>
							<v-card-text class="pa-4">
								<v-btn
									style="background-color: transparent; box-shadow: none; padding: 0; white-space: normal; display: block; text-align: left; justify-content: start;"
									:to="`/painel/projeto_lei/${projeto.projetos_leis_id.id}/${sanitizeTitle(projeto.projetos_leis_id.numero)}/${sanitizeTitle(vademecumSelecionado.titulo)}`"
								>
									<h4 class="text-h6 font-weight-bold mb-2" style="color: #424242;">
										{{ projeto.projetos_leis_id.numero }}
									</h4>
								</v-btn>
								<p class="text-body-1" style="color: #666;">
									{{ projeto.projetos_leis_id.nome }}
								</p>
							</v-card-text>
						</v-card>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- Header do Grupo -->
		<div class="group-header-card mb-4">
			<div class="d-flex align-center">
				<!-- Foto do Grupo ou Ícone de lâmpada -->
				<div class="group-image-container mr-4">
					<v-img 
						v-if="grupoComputed.imagem_grupo"
						:src="`https://api.mylegis.com.br/assets/${grupoComputed.imagem_grupo}`"
						width="64"
						height="64"
						contain
						class="rounded-sm group-image"
					/>
					<v-icon 
						v-else
						:color="dynamicColor" 
						size="48"
						class="group-icon"
					>
						mdi-lightbulb-outline
					</v-icon>
				</div>
				
				<!-- Nome do Grupo -->
				<div class="flex-grow-1">
					<h1 class="group-name" :style="{ color: dynamicColor }">
						{{ grupoComputed.nome_grupo }}
					</h1>
					
					<!-- Botões de Ação -->
					<div class="group-actions mt-2">
						<v-btn 
							v-if="isOwner" 
							outlined 
							small 
							:color="dynamicColor"
							@click="handleEdit"
							class="mr-2"
						>
							<v-icon small left>mdi-pencil</v-icon>
							EDITAR
						</v-btn>
						<v-btn 
							outlined 
							small 
							:color="dynamicColor"
							@click="showGroupDetails = true"
							class="mr-2"
						>
							<v-icon small left>mdi-information</v-icon>
							DETALHES
						</v-btn>
					
					</div>
				</div>
			</div>
		</div>

		<!-- Tabs de Navegação -->
		<v-tabs 
			v-if="grupoComputed.id"
			fixed-tabs 
			:color="dynamicColor" 
			:slider-color="dynamicColor"
			class="group-tabs"
		>
			<v-tab :to="`/painel/grupo/${grupoComputed.id}`" exact>
				<v-icon class="mr-2 tab-icon">mdi-rss</v-icon>
				FEED
			</v-tab>
			<v-tab :to="`/painel/grupo/${grupoComputed.id}/arquivos`">
				<v-icon class="mr-2 tab-icon">mdi-folder</v-icon>
				ARQUIVOS
			</v-tab>
			<v-tab :to="`/painel/grupo/${grupoComputed.id}/vademecun`">
				<v-icon class="mr-2 tab-icon">mdi-bookshelf</v-icon>
				VADE-MÉCUN
			</v-tab>
		</v-tabs>
	</div>
</template>

<script>
export default {
	name: 'GroupHeader',
	props: {
		grupo: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			localGrupo: null,
			edit: {},
			loadingEdit: false,
			showEditGroup: false,
			showGroupDetails: false,
			showVademecunsDialog: false,
			loadingVademecuns: false,
			vademecuns: [],
			showVademecumDetalhes: false,
			vademecumSelecionado: null
		};
	},
	computed: {
		grupoComputed() {
			return this.grupo || this.localGrupo || { administradores: [] };
		},
		isOwner() {
			return this.grupoComputed.user_created === this.$store.state.user.id;
		},
		user() {
			return this.$store.state.user;
		},
		dynamicColor() {
			return this.$route.params.id_grupo === '6f5ff131-006f-4525-9b17-75ae9674a09f' 
				? '#d8934e' 
				: '#F15A24';
		}
	},
	mounted() {
		// Só carregar grupo se houver id_grupo na rota
		if (this.$route.params.id_grupo && (!this.grupoComputed || !this.grupoComputed.id)) {
			this.reloadGrupo();
		}
	},
	watch: {
		'$route.params.id_grupo'() {
			// Só carregar grupo se houver id_grupo na rota
			if (this.$route.params.id_grupo && (!this.grupoComputed || !this.grupoComputed.id)) {
				this.reloadGrupo();
			}
		}
	},
	methods: {
		reloadGrupo() {
			if (!this.$route.params.id_grupo) {
				window.console.log('GroupHeader: id_grupo não encontrado na rota, pulando carregamento');
				return;
			}
			this.$http
				.get(
					'items/grupos/' +
					this.$route.params.id_grupo +
					'?fields=integrantes.*,administradores.*,administradores.directus_users_id.*,integrantes.directus_users_id.*,*'
				)
				.then((res) => {
					this.localGrupo = res.data.data;
					this.$emit('reload');
				})
				.catch((error) => {
					window.console.error('Erro ao carregar grupo:', error);
					// Não propagar o erro se não houver id_grupo
					if (!this.$route.params.id_grupo) {
						return;
					}
				});
		},
		handleEdit() {
			this.edit.nome_grupo = this.grupoComputed.nome_grupo;
			this.edit.restrito = this.grupoComputed.restrito;
			this.showEditGroup = true;
		},
		handleVademecuns() {
			this.showVademecunsDialog = true;
			this.loadVademecuns();
		},
		async updateGroup() {
			this.loadingEdit = true;
			try {
				let asset = undefined;
				if (this.edit.imagem_grupo) {
					asset = await this.uploadFile();
				}
				
				await this.$http.patch(`items/grupos/${this.$route.params.id_grupo}`, {
					nome_grupo: this.edit.nome_grupo,
					imagem_grupo: asset ? asset.id : undefined,
					restrito: this.edit.restrito
				});
				
				this.edit = {};
				this.showEditGroup = false;
				this.$emit('reload');
			} catch (error) {
				window.console.error('Erro ao atualizar grupo:', error);
			} finally {
				this.loadingEdit = false;
			}
		},
		async uploadFile() {
			if (!this.edit.imagem_grupo) return undefined;
			const formData = new FormData();
			formData.append('image', this.edit.imagem_grupo);
			const response = await this.$http.post('/files', formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});
			return response.data.data;
		},
		async loadVademecuns() {
			try {
				this.loadingVademecuns = true;
				const grupoId = this.$route.params.id_grupo;
				
				const { data } = await this.$http.get(
					`items/vademecums?fields=*,leis.id,leis.leis_id_lei.*,projetos_leis.*,projetos_leis.projetos_leis_id.*,user_created.id,user_created.first_name,user_created.last_name,user_created.avatar&filter[status][_eq]=public&filter[id_grupo][_null]=true&sort=-date_created`
				);

				const grupoRes = await this.$http.get(
					`items/grupos/${grupoId}?fields=integrantes.directus_users_id.id`
				);
				
				const integrantesIds = grupoRes.data.data.integrantes.map(i => i.directus_users_id.id);
				
				this.vademecuns = data.data.filter(v => {
					const userId = v.user_created && v.user_created.id ? v.user_created.id : v.user_created;
					return integrantesIds.includes(userId) && !v.id_grupo;
				});
			} catch (error) {
				window.console.error('Erro ao carregar vademecuns do grupo:', error);
			} finally {
				this.loadingVademecuns = false;
			}
		},
		verVademecum(vademecum) {
			this.vademecumSelecionado = vademecum;
			this.showVademecumDetalhes = true;
		},
		sanitizeTitle(title) {
			if (!title) return '';
			var slug = '';
			var titleLower = title.toLowerCase();
			slug = titleLower.replace(/e|é|è|ẽ|ẻ|ẹ|ê|ế|ề|ễ|ể|ệ/gi, 'e');
			slug = slug.replace(/a|á|à|ã|ả|ạ|ă|ắ|ằ|ẵ|ẳ|ặ|â|ấ|ầ|ẫ|ẩ|ậ/gi, 'a');
			slug = slug.replace(/o|ó|ò|õ|ỏ|ọ|ô|ố|ồ|ỗ|ổ|ộ|ơ|ớ|ờ|ỡ|ở|ợ/gi, 'o');
			slug = slug.replace(/u|ú|ù|ũ|ủ|ụ|ư|ứ|ừ|ữ|ử|ự/gi, 'u');
			slug = slug.replace(/đ/gi, 'd');
			slug = slug.replace(/\s*$/g, '');
			slug = slug.replace(/\s+/g, '-');
			return slug;
		}
	}
};
</script>

<style scoped>
.group-header {
	width: 100%;
}

.group-header-card {
	background: white;
	border-radius: 8px;
	padding: 20px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.group-name {
	font-size: 1.75rem;
	font-weight: bold;
	margin: 0;
	line-height: 1.2;
}

.group-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.group-tabs {
	margin-top: 0;
	--tab-active-color: v-bind('dynamicColor');
}

.group-tabs :deep(.v-tab) {
	text-transform: uppercase;
	font-weight: 500;
	font-size: 0.875rem;
}

.group-tabs :deep(.v-tab--active) {
	color: var(--tab-active-color) !important;
}

/* Padronizar cores dos ícones das abas */
.group-tabs :deep(.v-tab .tab-icon) {
	color: #757575 !important; /* Cinza para estado inativo */
	transition: color 0.2s ease;
}

.group-tabs :deep(.v-tab--active .tab-icon) {
	color: var(--tab-active-color) !important; /* Cor dinâmica para estado ativo */
}

.group-image-container {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 64px;
	height: 64px;
}

.group-image {
	object-fit: contain;
}

.group-icon {
	flex-shrink: 0;
}
</style>
